/**
 * Step 2 of the Obsidian import: create pages, properties, content and
 * attachments from an analysed vault. The source files are only read.
 */
import type { JSONContent } from '@tiptap/core';
import { ensureTaskIds, isAttachmentTarget } from '@nexus/editor';
import { parseMarkdown } from '@nexus/markdown';
import type { Store } from '@nexus/store';
import { mimeOf, type NoteInfo, type VaultAnalysis } from './analyze';
import { isEmptyDoc, jsonToUpdate, reindexPage } from './content';

export interface ImportOptions {
  /** Original front-matter key → Nexus property (null = skip). Unlisted keys are kept as-is. */
  mapping: Record<string, string | null>;
  includeConflicts: boolean;
  /** Put everything under one page with this title (null = at the root). */
  rootTitle: string | null;
  /** Folders whose pages get `ai: never` (never sent to AI). */
  aiExcludedFolders: string[];
  dailyFolder: string | null;
  templatesFolder: string | null;
  tasksFolder?: string | null;
}

export interface ImportProgress {
  phase: 'folders' | 'pages' | 'attachments' | 'content' | 'done';
  done: number;
  total: number;
}

export interface ImportResult {
  rootPageId: string | null;
  pages: number;
  attachments: number;
  unresolvedLinks: number;
  warnings: string[];
}

/** What a Notion export writes for an empty date property. */
export const INVALID_DATE = 'Invalid date';

const stripMd = (s: string) => s.replace(/\.md$/i, '');

/** Merge front matter under the mapping; the first non-empty value wins on collisions. */
export function mapFrontmatter(
  fm: Record<string, unknown>,
  mapping: Record<string, string | null>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(fm)) {
    const target = key in mapping ? mapping[key] : key;
    if (!target) continue;
    const empty = value === null || value === '' || (Array.isArray(value) && value.length === 0);
    if (target in out && (empty || !isEmpty(out[target]))) continue;
    out[target] = normalizeValue(target, value);
  }
  return out;
}

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

function normalizeValue(key: string, value: unknown): unknown {
  // YAML dates become Date objects; store them as ISO dates like Obsidian shows them.
  if (value instanceof Date) {
    const iso = value.toISOString();
    return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso.slice(0, 16);
  }
  if ((key === 'tags' || key === 'aliases') && typeof value === 'string') {
    return value.split(/[,\s]+/).filter(Boolean);
  }
  if (Array.isArray(value)) return value.map((v) => normalizeValue('', v));
  return value;
}

/** Link-target index with Obsidian's resolution rules. */
class NameIndex {
  private byName = new Map<string, { id: string; path: string }[]>();
  private byPath = new Map<string, string>();

  add(id: string, path: string, names: string[]) {
    this.byPath.set(stripMd(path).toLowerCase(), id);
    for (const n of names) {
      const key = n.toLowerCase();
      this.byName.set(key, [...(this.byName.get(key) ?? []), { id, path }]);
    }
  }

  resolve(target: string, fromFolder: string): string | null {
    const t = stripMd(target.trim()).replace(/^\/+/, '');
    if (t.includes('/')) {
      const exact = this.byPath.get(t.toLowerCase());
      if (exact) return exact;
      for (const [path, id] of this.byPath) if (path.endsWith(`/${t.toLowerCase()}`)) return id;
      // Paths from other tools (e.g. a Notion export) — fall back to the name.
      return this.resolve(t.split('/').pop()!, fromFolder);
    }
    const candidates = this.byName.get(t.toLowerCase()) ?? [];
    if (candidates.length <= 1) return candidates[0]?.id ?? null;
    const sameFolder = candidates.find(
      (c) => c.path.split('/').slice(0, -1).join('/') === fromFolder,
    );
    return (sameFolder ?? [...candidates].sort((a, b) => a.path.length - b.path.length)[0]!).id;
  }
}

function resolveDocLinks(
  doc: JSONContent,
  index: NameIndex,
  fromFolder: string,
  onUnresolved: () => void,
): JSONContent {
  const walk = (n: JSONContent): JSONContent => {
    if ((n.type === 'wikiLink' || n.type === 'embed') && n.attrs) {
      const target = n.attrs.target as string;
      if (n.type === 'embed' && isAttachmentTarget(target)) return n;
      if (!target.trim()) return n; // [[#Heading]] within the same page
      const pageId = index.resolve(target, fromFolder);
      if (!pageId) onUnresolved();
      return { ...n, attrs: { ...n.attrs, target: stripMd(target), pageId } };
    }
    return n.content ? { ...n, content: n.content.map(walk) } : n;
  };
  return walk(doc);
}

const underAny = (path: string, folders: string[]) =>
  folders.some((f) => path === f || path.startsWith(`${f}/`));

export async function runImport(
  store: Store,
  vault: VaultAnalysis,
  opts: ImportOptions,
  onProgress: (p: ImportProgress) => void = () => {},
): Promise<ImportResult> {
  const warnings: string[] = [];
  const notes = vault.notes.filter((n) => opts.includeConflicts || !n.isConflict);
  const root = opts.rootTitle
    ? await store.createPage({ title: opts.rootTitle, icon: '📥' })
    : null;

  // ---- folders (Obsidian shows folders first, alphabetically) --------------------
  const folderPages = new Map<string, string>();
  folderPages.set('', root?.id ?? '');
  const folderNotes = new Map<string, NoteInfo>(); // "A/B" → note "A/B/B.md"
  for (const n of notes) {
    if (n.folder && n.folder.split('/').pop() === n.title) folderNotes.set(n.folder, n);
  }
  const folders = vault.folders.filter((f) =>
    notes.some((n) => n.folder === f || n.folder.startsWith(`${f}/`)),
  );
  for (const [i, folder] of folders.entries()) {
    const parent = folderPages.get(folder.split('/').slice(0, -1).join('/')) || null;
    const page = await store.createPage({ title: folder.split('/').pop()!, parentId: parent });
    folderPages.set(folder, page.id);
    onProgress({ phase: 'folders', done: i + 1, total: folders.length });
  }

  // ---- pages + properties ---------------------------------------------------------
  const index = new NameIndex();
  const pageOf = new Map<string, string>(); // note path → page id
  const ordered = [...notes].sort((a, b) => a.path.localeCompare(b.path));
  for (const [i, note] of ordered.entries()) {
    const isFolderNote = folderNotes.get(note.folder) === note;
    const id = isFolderNote
      ? folderPages.get(note.folder)!
      : (
          await store.createPage({
            title: note.title,
            parentId: folderPages.get(note.folder) || null,
          })
        ).id;
    pageOf.set(note.path, id);
    const parsed = parseMarkdown(note.text);
    if (parsed.invalidFrontmatter !== null) {
      warnings.push(`${note.path}: front matter is geen geldige YAML en is als tekst bewaard`);
    }
    const props = mapFrontmatter(parsed.frontmatter ?? {}, opts.mapping);
    if (underAny(note.folder, opts.aiExcludedFolders)) props.ai = 'never';
    for (const [key, value] of Object.entries(props)) {
      // Notion exports write "Invalid date" for an empty date: no value at all.
      if (value === INVALID_DATE) continue;
      await store.setProp(id, key, value);
    }
    const aliases = Array.isArray(props.aliases) ? props.aliases.map(String) : [];
    index.add(id, note.path, [note.title, ...aliases]);
    onProgress({ phase: 'pages', done: i + 1, total: ordered.length });
  }
  // Folder pages are link targets too ([[Klanten]]), unless a note already claims the name.
  for (const [folder, id] of folderPages) {
    if (folder && !folderNotes.has(folder))
      index.add(id, `${folder}.md`, [folder.split('/').pop()!]);
  }
  for (const folder of opts.aiExcludedFolders) {
    const id = folderPages.get(folder);
    if (id) await store.setProp(id, 'ai', 'never');
  }

  // ---- attachments ------------------------------------------------------------------
  for (const [i, file] of vault.attachments.entries()) {
    await store.addAttachment({
      name: file.path.split('/').pop()!,
      path: file.path,
      mime: mimeOf(file.path),
      data: await file.read(),
    });
    onProgress({ phase: 'attachments', done: i + 1, total: vault.attachments.length });
  }

  // ---- content (links resolved against all imported pages) --------------------------
  let unresolved = 0;
  for (const [i, note] of ordered.entries()) {
    const id = pageOf.get(note.path)!;
    let doc = parseMarkdown(note.text).doc;
    const invalid = parseMarkdown(note.text).invalidFrontmatter;
    if (invalid !== null) {
      // Keep broken front matter visible instead of losing it.
      doc = {
        ...doc,
        content: [
          { type: 'rawBlock', attrs: { text: `---\n${invalid}\n---` } },
          ...(doc.content ?? []),
        ],
      };
    }
    doc = resolveDocLinks(doc, index, note.folder, () => unresolved++);
    doc = ensureTaskIds(doc).doc;
    // Notes with only front matter stay without content, like pages never edited.
    if (!isEmptyDoc(doc)) await store.appendContent(id, jsonToUpdate(doc));
    await reindexPage(store, id, doc);
    onProgress({ phase: 'content', done: i + 1, total: ordered.length });
  }

  if (opts.dailyFolder && folderPages.get(opts.dailyFolder)) {
    await store.setSetting('daily_parent_id', folderPages.get(opts.dailyFolder)!);
  }
  if (opts.tasksFolder && folderPages.get(opts.tasksFolder)) {
    await store.setSetting('tasks_parent_id', folderPages.get(opts.tasksFolder)!);
  }
  if (opts.templatesFolder && folderPages.get(opts.templatesFolder)) {
    await store.setSetting('templates_parent_id', folderPages.get(opts.templatesFolder)!);
  }
  await store.setSetting('last_import_at', String(Date.now()));

  onProgress({ phase: 'done', done: 1, total: 1 });
  return {
    rootPageId: root?.id ?? null,
    pages: ordered.length + folders.length - folderNotes.size,
    attachments: vault.attachments.length,
    unresolvedLinks: unresolved,
    warnings,
  };
}
